import { useState, useEffect } from "react";
import { Flag } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";

export default function ReportButton({ postId, className = "" }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [sending, setSending] = useState(false);
  const [reasons, setReasons] = useState([]);

  useEffect(() => {
    if (!open || reasons.length) return;
    api.get("/report-reasons").then((r) => setReasons(r.data)).catch(() => {});
  }, [open, reasons.length]);

  const submit = async () => {
    if (!user || user === false) {
      nav("/login");
      return;
    }
    if (!reason) {
      toast.error("Please pick a reason");
      return;
    }
    setSending(true);
    try {
      await api.post(`/posts/${postId}/report`, { reason, details: details.trim() || undefined });
      toast.success("Report sent — thank you!");
      setOpen(false);
      setReason("");
      setDetails("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          className={`flex items-center gap-1.5 text-sm text-[#857163] hover:text-red-500 transition ${className}`}
          data-testid={`report-btn-${postId}`}
        >
          <Flag className="w-4 h-4" />
          <span className="hidden sm:inline">Report</span>
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[#FFFBF5] border-[#EBDCD1] rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-[#3B2C24]">Report this post</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-[#857163] -mt-2">
          Help us keep Cake Pop Talk sweet. Pick a reason.
        </p>
        <div className="space-y-2" data-testid="report-reasons-list">
          {reasons.map((r) => (
            <label
              key={r.key}
              className={`flex items-center gap-3 p-3 rounded-2xl border cursor-pointer transition ${
                reason === r.key ? "border-[#FF7E67] bg-[#FF7E67]/5" : "border-[#EBDCD1] hover:bg-[#F9EFE6]"
              }`}
              data-testid={`report-reason-${r.key}`}
            >
              <input
                type="radio"
                name="report-reason"
                value={r.key}
                checked={reason === r.key}
                onChange={() => setReason(r.key)}
                className="accent-[#FF7E67]"
              />
              <span className="text-sm text-[#3B2C24]">{r.label}</span>
            </label>
          ))}
        </div>
        <textarea
          className="cp-input min-h-[70px] resize-none text-sm"
          placeholder="Optional: add more details…"
          value={details}
          onChange={(e) => setDetails(e.target.value)}
          data-testid="report-details-input"
        />
        <div className="flex justify-end gap-2">
          <button onClick={() => setOpen(false)} className="cp-btn-ghost">Cancel</button>
          <button onClick={submit} disabled={sending || !reason} className="cp-btn-primary disabled:opacity-50" data-testid="report-submit-btn">
            {sending ? "Sending…" : "Send report"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
